import { Link } from '@ps/web-shared-components';
import classNames from 'clsx';
import { get, kebabCase } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import URL from 'url-parse';

import type { CasinoIcon } from '@swf/casino';
import {
  CasinoMultiGameImage,
  getIconFallback,
  getIconWidth,
  getRenderIcon,
  iconMeta,
  needsVideoTag
} from '@swf/casino';
import LazyImage from '@swf/components/LazyImage/LazyImage';
import FSBIcon from '@swf/core/assets/casino/fsb-placeholder.png';
import useTouchSupported from '@swf/core/hooks/useTouchSupported';
import { select } from '@swf/core/store';
import type { CasinoGame, MosaicContext } from '@swf/core/types/CasinoGame';
import type { CasinoRace } from '@swf/core/types/CasinoRaces';

import styles from './CasinoGameIcon.scss';
import IconOverlay from './components/IconOverlay';

export interface Props {
  className?: string;
  disabled?: boolean;
  game?: CasinoGame;
  images?: CasinoGame['images'];
  race?: CasinoRace;
  FSBPlaceholder?: boolean;
  icon?: CasinoIcon;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  preload?: boolean;
  innerRef?: React.Ref<HTMLDivElement>;
  scale?: boolean;
  tooltip?: string;
  visible?: boolean;
  mosaicHint?: MosaicContext;
}

const CasinoGameIcon: React.FC<React.PropsWithChildren<Props>> = ({
  icon = 'icnSquare',
  images,
  preload,
  game,
  race,
  scale = true,
  disabled,
  tooltip,
  innerRef,
  visible,
  FSBPlaceholder,
  mosaicHint,
  ...rest
}) => {
  const isMobile = useSelector(select.device.isMobile);
  const isTouchSupported = useTouchSupported();
  const videoRef = React.createRef<HTMLVideoElement>();
  const { name = '' } = game || {};
  const { liveRaceImgUrl = null, multiGameList = [] } = race || {};
  const iconFallback = useMemo(() => getIconFallback(icon), [icon]);
  const fallback = FSBPlaceholder ? FSBIcon : iconFallback;
  const validImages = useMemo(() => images || game?.images || {}, [images, game]);
  const animatedSrc = iconMeta[icon]?.animated && validImages[icon];
  const width = useMemo(() => getIconWidth(icon, isMobile), [icon, isMobile]);
  const gameIcon = useMemo(
    () => getRenderIcon(icon, isMobile, validImages),
    [icon, isMobile, validImages]
  );
  const raceIcon = icon === 'icnRace';
  let src = gameIcon || fallback;
  const aliases = useSelector(select.cms.getAliases);
  const routeAvailable = aliases && aliases.casinoGameInfo;

  if (width && src !== fallback && scale) {
    const url = new URL(src);
    if (game) {
      url.set('query', {
        imwidth: Math.floor(width).toString(),
        id: `${game.gametype}${game.variant}`
      });
    }
    src = url.toString();
  }

  const props = { className: styles.icon, src };
  const fallbackElem = <img {...props} alt={tooltip || name} src={fallback} />;
  const containerProps: React.HTMLAttributes<HTMLDivElement> = {};
  let iconElem;

  if (!isMobile && animatedSrc && needsVideoTag(animatedSrc)) {
    // eslint-disable-next-line jsx-a11y/media-has-caption
    iconElem = <video {...props} ref={videoRef} />;
    containerProps.onMouseEnter = () => videoRef.current?.play();
    containerProps.onMouseLeave = () => {
      videoRef.current?.pause();
      videoRef.current && (videoRef.current.currentTime = 0);
    };
  } else if (preload) {
    // suppress hydration warning due to protocol mismatch (// on server vs https:// on client)
    iconElem = <img {...props} alt={tooltip || name} suppressHydrationWarning />;
    // TODO: remove with races redesign cleanup
  } else if (!liveRaceImgUrl && !raceIcon && multiGameList?.length > 1) {
    let image, imageURL;
    const imageList: string[] =
      multiGameList?.map((x) => {
        image = get(x.images, [icon], fallback);
        imageURL = new URL(image);
        imageURL.set('query', {
          imwidth: Math.floor(width as number).toString(),
          id: `${game?.gametype}${game?.variant}`
        });
        return imageURL.toString();
      }) || [];
    iconElem = <CasinoMultiGameImage multiGameList={imageList} alt={tooltip || name} />;
  } else {
    iconElem = (
      <LazyImage
        {...props}
        fallback={fallbackElem}
        loader={fallbackElem}
        visible={visible}
        alt={tooltip || name}
      />
    );
  }

  useEffect(() => {
    const { current: videoElement } = videoRef;
    videoElement?.setAttribute('muted', '');
  }, [videoRef]);

  return (
    <div
      onClick={rest.onClick}
      className={classNames(
        styles.container,
        (!game || disabled) && styles.disabled,
        isTouchSupported && styles.touch,
        rest.className
      )}
      ref={innerRef}
      {...containerProps}
    >
      {src === fallback && <div className={styles.iconLabel}>{name}</div>}
      {routeAvailable && game ? (
        <Link
          className={styles.link}
          track={false}
          onClick={(event: React.MouseEvent) => event.preventDefault()}
          alias="casinoGameInfo"
          values={{ name: kebabCase(game.name), gtype: game.gametype, vtype: game.variant }}
        >
          <IconOverlay tags={game.tags} icon={icon} mosaicHint={mosaicHint} />
          {iconElem}
        </Link>
      ) : (
        <>
          <IconOverlay tags={game?.tags} icon={icon} mosaicHint={mosaicHint} />
          {iconElem}
        </>
      )}
      {rest.children}
    </div>
  );
};

export default CasinoGameIcon;
